import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { toastError, toastSuccess } from '@components/Toast';

import EODate from '@features/collection/components/EODate';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { clearSampleRequest, fetchSampleProductList } from '@store/actions/SampleAction';

import { _getKey, getTodaysDate, packTag, renderSetImages, setTag } from '@utils/Util';

import CustomDropdown from '@/components/CustomDropdown';
import {
	useApproveCostingRequestMutation,
	useCreateCostingRequestMutation,
	useGetAllBrandsQuery
} from '@/api/commonApis';
import { formatBrandOptions } from '@/features/sampleDevelopment/utils';
import ModalContainer from '@/components/modals/ModalContainer';

const INITIAL_VALUES = {
	brand: null,
	costingCompletingUserType: null,
	estimatedOrderDeliveryDate: null,
	requiredDate: ''
};

const COMPLETING_USER_TYPES = [
	{ value: 'By Costing team', key: 'COSTING_MANAGER', label: 'By Costing team' },
	{ value: 'By AM team', key: 'ACCOUNT_MANAGER', label: 'By AM team' }
];

const CostingAdd = ({ selectedDesignIds, onClose, isEdit, requestFrom }) => {
	const [selectedDesigns, setSelectedDesigns] = useState([]);
	const sampleStore = useSelector((store) => store.sampleStore);
	const { costingDetails } = useSelector((store) => store.costingStore);
	const isLoading = useSelector((store) => store.loaderStore);
	const [errors, setErrors] = useState([]);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { id } = useParams();
	const [selectedQty, setSelectedQty] = useState('');
	const { data: brands } = useGetAllBrandsQuery({ params: { page: 0, size: 1000 } });
	const [headerSectionErrors, setHeaderSectionErrors] = useState(null);
	const [inputValues, setInputValues] = useState(INITIAL_VALUES);

	const [approveCostingRequest] = useApproveCostingRequestMutation();
	const [createCostingRequest] = useCreateCostingRequestMutation();

	useEffect(() => {
		if (isEdit) {
			setSelectedDesigns(costingDetails?.quoteItemResponseList);
		} else {
			setSelectedDesigns(sampleStore.selectedProducts);
		}
	}, [sampleStore, isEdit]);

	useEffect(() => {
		if (isEdit) {
			const formatDateForInput = (dateString) => {
				if (!dateString) return '';
				const date = new Date(dateString);
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const day = String(date.getDate()).padStart(2, '0');
				return `${year}-${month}-${day}`;
			};

			setInputValues({
				brand: {
					id: costingDetails?.brandId,
					name: costingDetails?.brandName,
					value: costingDetails?.brandName
				},
				costingCompletingUserType: null,
				estimatedOrderDeliveryDate: costingDetails?.estimatedOrderDeliveryDate,
				requiredDate: formatDateForInput(costingDetails?.requestedDate)
			});
		}
	}, [isEdit, costingDetails?.id]);

	useEffect(() => {
		dispatch(openLoader());
		dispatch(fetchSampleProductList(selectedDesignIds)).finally(() => {
			dispatch(closeLoader());
		});
	}, []);

	const onUpdateError = (value, id, type) => {
		let allErrors = [...errors];
		if (value) {
			let updated = allErrors.filter((item) => {
				if (parseInt(item.errorDesignId) === parseInt(id) && item.type !== type) {
					return item;
				}
			});
			setErrors(updated);
		}
	};

	const onChangeItem = (e, id) => {
		let { name, value } = e.target;
		let updatedDesigns = selectedDesigns;
		updatedDesigns.forEach((item) => {
			if (item.id === id) {
				item[name] = value;
			}
			setSelectedQty(value);
			onUpdateError(value, item.id, 'QUANTITY');

			return item;
		});
		setSelectedDesigns([...updatedDesigns]);
	};

	const onBlurQuantityField = (id) => {
		let updatedDesigns = selectedDesigns;
		updatedDesigns.forEach((item) => {
			if (!item.quantity && item.id !== id) {
				item['quantity'] = selectedQty;
			}
			return item;
		});
		setSelectedDesigns([...updatedDesigns]);
	};

	const isValidForm = (body) => {
		setErrors([]);
		let isValid = true;
		body.forEach((item) => {
			// if (!item.requiredDate) {
			// 	setErrors((old) => [...old, { errorDesignId: item.productId, type: 'DATE' }]);
			// 	isValid = false;
			// }
			if (!item.quantity) {
				setErrors((old) => [...old, { errorDesignId: item.productId, type: 'QUANTITY' }]);
				isValid = false;
			}
		});
		return isValid;
	};

	const isValidHeaderSection = () => {
		let errors = {};

		if (!inputValues?.brand) {
			errors['brand'] = 'Brand is required';
		}
		if (!inputValues?.costingCompletingUserType) {
			errors['costingCompletingUserType'] = 'Costing Completing User Type is required';
		}
		if (!inputValues?.estimatedOrderDeliveryDate) {
			errors['estimatedOrderDeliveryDate'] = 'Estimated Order Delivery Date is required';
		}
		setHeaderSectionErrors(errors);
		return Object.keys(errors).length === 0;
	};
	const onGetQuantityError = (item) => {
		return errors.some((err) => err?.errorDesignId === item.id && err?.type === 'QUANTITY');
	};

	const getFomattedData = () => {
		let data = selectedDesigns?.filter((design) => selectedDesignIds?.includes(design?.productId));
		if (requestFrom === 'PENDING_REQUEST') {
			data?.map((item) => {
				return {
					productId: item.id,
					quantity: item.quantity,
					isSet: !!item.isSet,
					setMemberIds: item?.setMemberProductIds
				};
			});
		} else {
			data?.filter((design) => selectedDesignIds.includes(design?.productId))?.map((item) => {
				return {
					productId: item.id,
					quantity: item.quantity,
					isSet: !!item.isSet,
					setMemberIds: item?.setMemberProductIds
				};
			});
		}

		return data;
	};

	const costingRequestPayload = () => {
		return selectedDesigns?.map((item) => ({
			productId: item?.id,
			quantity: item?.quantity,
			isSet: item?.isSet,
			setMemberIds: item?.setMemberProducts?.map((member) => member?.id)
		}));
	};

	const onSubmitCosting = async () => {
		let payload = getFomattedData();
		const isValid = isValidForm(payload);
		const isHeaderValid = isValidHeaderSection();

		if (!isValid || !isHeaderValid) return;
		setErrors([]);
		dispatch(openLoader());
		const body = {
			// quoteItemRequestList: payload,
			estimatedOrderDeliveryDate: inputValues.estimatedOrderDeliveryDate || null,
			costingCompletingUserType: inputValues.costingCompletingUserType?.key,
			entityType: requestFrom === 'FAVOURITE' ? 'FAVOURITE_FOLDER' : 'COLLECTION',
			entityId: costingDetails?.folderId || id,
			brandId: inputValues?.brand?.id,
			requiredDate: inputValues?.requiredDate || null
		};
		if (requestFrom === 'COLLECTION' || requestFrom === 'FAVOURITE') {
			createCostingRequest({ body: { ...body, quoteItemRequest: costingRequestPayload() } })
				.unwrap()
				.then((res) => {
					dispatch(closeLoader());
					dispatch(clearSampleRequest());
					// navigate(`/costing/${id}`);
					toastSuccess(res?.message);
					onClose();
				})
				.catch((error) => {
					dispatch(closeLoader());
					toastError(error?.data?.message || 'Something went wrong!');
				});
		} else {
			approveCostingRequest({
				quoteId: id,
				body: { ...body, quoteItemRequestList: payload }
			})
				.unwrap()
				.then((res) => {
					dispatch(closeLoader());
					dispatch(clearSampleRequest());
					navigate(`/costing/${id}`);
					toastSuccess(res?.message);
					onClose();
				})
				.catch((error) => {
					dispatch(closeLoader());
					toastError(error?.response?.data?.message || 'Something went wrong!');
				});
		}
	};

	const isValidQuantity = (e) => {
		const valuesAllowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ','];
		if (!valuesAllowed.includes(e.key)) {
			e.preventDefault();
		}
	};

	const onCancelPopUp = () => {
		onClose('CANCEL');
	};

	const handleChange = (field, value) => {
		setInputValues((prev) => ({
			...prev,
			[field]: value
		}));
		setHeaderSectionErrors((prev) => ({
			...prev,
			[field]: ''
		}));
	};

	const getFormattedData = () => {
		if (requestFrom === 'PENDING_REQUEST') {
			return selectedDesigns?.filter((design) => selectedDesignIds.includes(design?.productId));
		} else {
			return selectedDesigns;
		}
	};

	return (
		<ModalContainer show={true} onHide={onCancelPopUp} backdrop="static" className="width-784-dialog">
			<div className="common-popup add-sample-details-popup shadow-24dp">
				<div className="common-popup-header d-flex justify-content-between">
					<div className="popup-tilte">
						<h3 className="semibold-16 mb-0">Costing Request </h3>
					</div>

					<div className="close-btn" onClick={onCancelPopUp}>
						<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
					</div>
				</div>
				<Modal.Body>
					<div className="common-popup-body scroll-y-label pb-0">
						<div className="sample-details-table">
							<div>
								<div className="flied-item d-grid grid-columns-2 gap-3">
									<div className="form-group">
										<label htmlFor="po-number">Brand*</label>
										<CustomDropdown
											title="Select target customer"
											onItemClick={(value) => handleChange('brand', value)}
											selectedItem={inputValues?.brand?.name}
											items={formatBrandOptions(brands)}
										/>
										{headerSectionErrors?.brand && (
											<p className="error">{headerSectionErrors?.brand}</p>
										)}
									</div>

									<div className="form-group">
										<label htmlFor="po-number">Who will complete?</label>
										<CustomDropdown
											title="Select.."
											onItemClick={(value) =>
												handleChange('costingCompletingUserType', value)
											}
											selectedItem={inputValues?.costingCompletingUserType?.label}
											items={COMPLETING_USER_TYPES}
										/>
										{headerSectionErrors?.costingCompletingUserType && (
											<p className="error">
												{headerSectionErrors?.costingCompletingUserType}
											</p>
										)}
									</div>

									<div className="form-group">
										<EODate
											eoDate={inputValues.estimatedOrderDeliveryDate}
											setEoDate={(val) => {
												handleChange('estimatedOrderDeliveryDate', val);
											}}
											isEoDateError={headerSectionErrors?.estimatedOrderDeliveryDate}
										/>
									</div>

									<div className="form-group">
										<div>
											<label htmlFor="requiredDate">Required Date</label>
											<input
												type="date"
												name="requiredDate"
												min={getTodaysDate()}
												value={inputValues?.requiredDate}
												onChange={(e) => handleChange('requiredDate', e.target.value)}
											/>
										</div>
									</div>
								</div>
							</div>
							<table className="table with-border">
								<thead>
									<tr>
										<th className="design-details-column">
											<span className="ml-2">
												{selectedDesignIds.length > 1 ? 'Designs' : 'Design'}
												{`(${selectedDesignIds.length})`}
											</span>
										</th>
										<th className="sample-details-column">Qty</th>
										{/* <th className="date-column">Required date</th> */}
									</tr>
								</thead>
								<tbody>
									{getFormattedData().map((item, idx) => (
										<tr key={item.id}>
											<td className="design-details-column ">
												<div className="d-flex align-items-center">
													{item.isSet ? (
														renderSetImages(
															isEdit
																? item?.featureImageDocUrl
																: item?.designDocuments
														)
													) : (
														<img
															src={
																isEdit
																	? item?.featureImageDocUrl
																	: item?.designDocuments[0]?.docUrl
															}
															alt="image"
															className="cell-img pr-2"
														/>
													)}
													{item.isSet ? (
														<div className="ml-12">
															{setTag()}
															<ul className="common-list-component my-1">
																{item?.setMemberProducts?.length > 1 && (
																	<li>
																		{isEdit
																			? item?.setMemberProducts[0]
																					?.market
																			: item?.setMemberProducts[0]
																					?.productGroup}
																	</li>
																)}
																{item.setMemberProducts &&
																	item.setMemberProducts.length > 1 &&
																	item?.setMemberProducts.map((member) => (
																		<li key={_getKey()}>
																			{isEdit
																				? member?.market
																				: member?.category}
																		</li>
																	))}
															</ul>
															<p className="regular-12 gray_dark_01">
																{isEdit
																	? item?.productRefNo
																	: item?.referenceNumber}
															</p>
														</div>
													) : (
														<div className="ml-12">
															{item.isPack && packTag()}
															<p className="regular-12 gray_dark_01 py-1">
																{isEdit
																	? item?.productRefNo
																	: item?.referenceNumber || item?.name}
															</p>
															<ul className="common-list-component mb-2">
																<li>{item?.productGroup || item?.market}</li>
																<li>{item?.category}</li>
															</ul>
														</div>
													)}
												</div>
											</td>
											<td>
												<input
													className={`${
														onGetQuantityError(item) && 'common-border-error'
													}`}
													type="text"
													autoComplete="off"
													name="quantity"
													autoFocus={idx === 0 && true}
													value={item?.quantity || ''}
													onChange={(e) => {
														onChangeItem(e, item?.id);
													}}
													onBlur={() => onBlurQuantityField(item?.id)}
													onKeyPress={isValidQuantity}
												/>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<div className="footer-buttons text-right">
						<button className="button gray-outline size32" onClick={onCancelPopUp}>
							<span className="gray_dark_02">Cancel</span>
						</button>
						<button
							className="button size32 ml-2"
							onClick={() => {
								onSubmitCosting();
							}}
							disabled={isLoading}
						>
							Submit
						</button>
					</div>
				</Modal.Footer>
			</div>
		</ModalContainer>
	);
};
export default CostingAdd;
